import baseApi from '@/modules/services/base';
import config from '@/lib/config/index';
import {
  AdminSignedCookieTag,
  AuthTag,
  SignedCookieTag,
  TenantProfileTag,
  TenantTag,
  TenantsTag,
  UserInfoTag,
} from './tags';
import ElaPermissions from '@/lib/auth/permissions/permission.type';
import { Roles } from '@/lib/auth/roles/roles.type';
import { getContentTypeResponse } from '@/lib/util';

interface GetSignedCookieQueryArgumentsInterface {
  tenantId: string;
  type: 'draft' | 'review' | 'live' | 'scorm';
  elearningId: string;
  elearningIdentifier?: string;
  quizIdentifier?: string;
  themeId?: string;
  cid?: number;
  mid?: number;
  token?: string;
}

interface TenantResponse {
  createdAt: Date;
  identifier: string;
  isDeleted: boolean | null;
  modifiedAt: Date;
  namespace: string;
  subdomain: string | null;
  tenantId: string;
}

export type CapabilityAssessor = 'email';
export type CapabilityAssessorType = 'MIGRATION';

interface CapabilitiesResponse {
  roles: Roles[];
  assessor: CapabilityAssessorType;
  assessorType: CapabilityAssessorType;
  elaCapabilities: ElaPermissions[];
}

interface CkfinderJwtResponse {
  CKFC: string;
}

interface BFFUserInfoType {
  authenticated: boolean;
  loginExpiry: number;
  oauthHost: string;
  user: any;
}

export interface TenantProfileType {
  tenantId: string;
  name: string;
  createdAt: Date;
  modifiedAt: Date;
  isDeleted: boolean | null;
  email: string;
  lcmsId: number;
  tenant: {
    createdAt: Date;
    identifier: string;
    isDeleted: boolean | null;
    modifiedAt: Date;
    namespace: string;
    subdomain: string | null;
    tenantId: string;
  };
  lcmsCompanyConfig: {
    subdomain: string;
  };
  configMetadata: {
    multiLang: string;
    defaultPlayerType: number;
    mobileContentEnable: number;
    migratedToNewDomain: number;
    useNewDomain: number;
    allowCloseQuiz: number;
    enableCourseLibrary: number;
    enableVideoLibrary: number;
    enableBreathe: number;
    courseTrackingEnable: number;
    regions: string[];
  };
  elearningCategoryAccess: string;
  plan: number;
  scorm: number;
  demoWelcomeMessage: string;
  elearningBookmarkEnable: number;
  // Live company-level "Allow AI Features" toggle (LMS2-5047), read from TMS by the
  // API on each fetch (not persisted config). Gates AI features like PPT Import.
  // Optional: absent on API builds that predate the field (deploy-order safe).
  aiEnabled?: boolean;
}

const authApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getUserInfo: build.query<BFFUserInfoType, void>({
      query: () => config.getUserInfoURL,
      providesTags: [UserInfoTag],
    }),
    getToken: build.query<any, void>({
      query: () => ({
        url: config.getTokenURL,
        responseHandler: getContentTypeResponse,
      }),
      providesTags: [AuthTag],
    }),
    getSignedCookies: build.query<any, GetSignedCookieQueryArgumentsInterface>({
      query: (params) => ({
        url: '/signed-cookies/generate',
        params,
      }),
      providesTags: [SignedCookieTag],
    }),
    getSignedCookiesAdmin: build.query<
      any,
      GetSignedCookieQueryArgumentsInterface
    >({
      query: (params) => ({
        url: '/signed-cookies/admin/generate',
        params,
      }),
      providesTags: [AdminSignedCookieTag],
    }),
    getTenants: build.query<TenantResponse[], void>({
      query: () => '/tenants',
      providesTags: [TenantsTag],
    }),
    getTenant: build.query<TenantResponse, string>({
      query: (id) => `/tenants/${id}`,
      providesTags: [TenantTag],
    }),
    getCurrentTenantProfile: build.query<TenantProfileType, void>({
      query: () => `/tenant-profiles/get-current`,
      transformResponse: (response: TenantProfileType) => {
        // makes sure response is number for some keys in configMetadata
        const configKeys = [
          'allowCloseQuiz',
          'courseTrackingEnable',
          'defaultPlayerType',
          'enableBreathe',
          'enableCourseLibrary',
          'enableVideoLibrary',
          'migratedToNewDomain',
          'mobileContentEnable',
          'useNewDomain',
        ];

        const configMetadata: { [key: string]: string | number | string[] } = {
          ...response.configMetadata,
        };
        configKeys.forEach((configKey) => {
          if (
            configMetadata[configKey] !== null &&
            configMetadata[configKey] !== undefined &&
            Number.isNaN(Number(configMetadata[configKey])) === false
          ) {
            configMetadata[configKey] = Number(configMetadata[configKey]);
          }
        });

        return {
          ...response,
          configMetadata,
        } as TenantProfileType;
      },
      providesTags: [TenantProfileTag],
    }),
    updateTenantProfile: build.mutation<any, TenantProfileType>({
      query: (body) => ({
        url: `/tenant-profiles/${body.tenantId}`,
        body,
        method: 'PUT',
      }),
      invalidatesTags: [TenantProfileTag],
    }),
    getCapabilities: build.query<CapabilitiesResponse, string>({
      query: () => '/users/capabilities',
      providesTags: [],
    }),
    getCkfinderJwt: build.query<CkfinderJwtResponse, string>({
      query: () => '/signed-cookies/ckfinder-token/generate',
      providesTags: [],
    }),
    // Grants the current tenant read access to its own /content/ assets.
    // The response body is unused; the value is the Set-Cookie headers.
    getContentSignedCookies: build.query<{ message: string }, string>({
      query: () => '/signed-cookies/content/generate',
      providesTags: [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTokenQuery,
  useGetSignedCookiesQuery,
  useGetTenantsQuery,
  useGetTenantQuery,
  useGetCurrentTenantProfileQuery,
  useUpdateTenantProfileMutation,
  useGetCapabilitiesQuery,
  useGetSignedCookiesAdminQuery,
  useLazyGetSignedCookiesAdminQuery,
  useGetCkfinderJwtQuery,
  useGetContentSignedCookiesQuery,
  useGetUserInfoQuery,
} = authApi;

export default authApi;
